import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import { AdminUser } from '../../types/admin/AdminTypes';

interface UsersTableProps {
  users: AdminUser[];
  onStatusChange?: (userId: string, status: 'active' | 'inactive' | 'suspended') => void;
  onGrantFreeAccess?: (userId: string) => void;
  onRevokeFreeAccess?: (userId: string) => void;
  onResetPassword?: (userId: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filterPlan: string;
  onFilterPlanChange: (value: string) => void;
  filterStatus: string;
  onFilterStatusChange: (value: string) => void;
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'default';
    case 'suspended':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'active':
      return 'Ativo';
    case 'inactive':
      return 'Inativo';
    case 'suspended':
      return 'Suspenso';
    default:
      return status;
  }
};

const getPlanColor = (plan: string) => {
  switch (plan) {
    case 'pro':
      return 'primary';
    case 'enterprise':
      return 'secondary';
    case 'free':
    default:
      return 'default';
  }
};

const getPlanLabel = (plan: string) => {
  switch (plan) {
    case 'pro':
      return 'Pro';
    case 'enterprise':
      return 'Enterprise';
    case 'free':
    default:
      return 'Free';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const formatCurrency = (value?: number) => {
  if (!value) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onStatusChange,
  onGrantFreeAccess,
  onRevokeFreeAccess,
  onResetPassword,
  searchTerm,
  onSearchChange,
  filterPlan,
  onFilterPlanChange,
  filterStatus,
  onFilterStatusChange,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <PeopleIcon sx={{ color: '#1976d2' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Usuários
        </Typography>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar usuário..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={filterPlan}
            onChange={(e) => onFilterPlanChange(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Todos os planos</MenuItem>
            <MenuItem value="free">Free</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
            <MenuItem value="enterprise">Enterprise</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={filterStatus}
            onChange={(e) => onFilterStatusChange(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Todos os status</MenuItem>
            <MenuItem value="active">Ativo</MenuItem>
            <MenuItem value="inactive">Inativo</MenuItem>
            <MenuItem value="suspended">Suspenso</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Plano</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Acesso Gratuito</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>MRR</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Criado em</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={getPlanLabel(user.plan)}
                    color={getPlanColor(user.plan) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(user.status)}
                    color={getStatusColor(user.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.hasFreeAccess ? (
                    <Tooltip title={user.freeAccessReason || 'Acesso gratuito liberado'}>
                      <Chip
                        label="Liberado"
                        color="success"
                        size="small"
                        icon={<CardGiftcardIcon />}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      label="Não"
                      color="default"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>{formatCurrency(user.mrr)}</TableCell>
                <TableCell>{formatDate(user.createdAt)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {user.status === 'active' ? (
                      <Tooltip title="Suspender usuário">
                        <IconButton
                          size="small"
                          onClick={() => onStatusChange?.(user.id, 'suspended')}
                          color="error"
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Ativar usuário">
                        <IconButton
                          size="small"
                          onClick={() => onStatusChange?.(user.id, 'active')}
                          color="success"
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {user.hasFreeAccess ? (
                      <Tooltip title="Revogar acesso gratuito">
                        <IconButton
                          size="small"
                          onClick={() => onRevokeFreeAccess?.(user.id)}
                          color="warning"
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Liberar acesso gratuito">
                        <IconButton
                          size="small"
                          onClick={() => onGrantFreeAccess?.(user.id)}
                          color="primary"
                        >
                          <CardGiftcardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Resetar senha">
                      <IconButton
                        size="small"
                        onClick={() => onResetPassword?.(user.id)}
                        color="secondary"
                      >
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={Math.max(page - 1, 0)}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Linhas por página"
      />

      {users.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Nenhum usuário encontrado
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default UsersTable;
